import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import grey from '@mui/material/colors/grey';

import { Text, View } from '../../../components/Base';
import {
  DEPOT_NUMBER,
  NO_PARCELS_FOUND,
  ROUTE,
  STOP,
  VIEW,
} from '../../../constants/strings';
import palette from '../../../components/styles/palette';
import { parseQuery, stringifyQuery } from '../../../utils/query';
import {
  clearSelection,
  updateSelectedStops,
} from '../../../store/reducers/ui/routeDetail/stopFilters';
import { ConditionalWrapper } from '../../../components/ConditionalWrapper';
import { getUserDepots } from '../../../store/selectors/user';
import { ROUTES_PAGE } from '../../../constants/routes';
import useAsyncNavigate from '../../../hooks/useAsyncNavigate';

const useStyles = makeStyles(({ typography }) => ({
  container: {
    overflowY: 'auto',
    height: typography.pxToRem(206),
  },
  errorText: {
    fontSize: typography.pxToRem(12),
    color: palette.dpdRed,
  },
  headerRow: {
    backgroundColor: grey['200'],
  },
  headerCell: {
    fontWeight: 500,
    fontSize: typography.pxToRem(12),
    lineHeight: typography.pxToRem(14),
    textTransform: 'capitalize',
    color: 'black',
    boxSizing: 'border-box',
    padding: `${typography.pxToRem(13)} ${typography.pxToRem(10)}`,
    width: '30%',
  },
  actionHeaderCell: {
    width: '10%',
  },
  blueButton: {
    color: palette.btn.blueFill,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: palette.btn.blueFill,
    },
    '&:disabled': {
      textDecoration: 'none',
      color: palette.typography.black.disabled,
      cursor: 'default',
    },
    transition: 'color 0.3s ease, text-decoration 0.3s ease',
  },
  bodyCell: {
    fontWeight: 'normal',
    fontSize: typography.pxToRem(12),
    lineHeight: typography.pxToRem(14),
    boxSizing: 'border-box',
    padding: `${typography.pxToRem(4)} ${typography.pxToRem(10)}`,
  },
}));

// eslint-disable-next-line react/display-name
const ParcelSearchResultRow = memo(
  ({ row, classes, handleNavigate, depots }) => {
    const {
      depotCode,
      routeCode,
      displayNumber,
      sequenceNumber,
      routeId,
      stopId,
    } = row;

    const enableNavigation = useMemo(
      () =>
        depots.some(
          ({ depotCode: userDepotCode }) => userDepotCode === depotCode
        ),
      [depots, depotCode]
    );

    return (
      <TableRow>
        <TableCell className={classes.bodyCell}>{depotCode}</TableCell>
        <TableCell className={classes.bodyCell}>{routeCode}</TableCell>
        <TableCell className={classes.bodyCell}>
          {displayNumber || sequenceNumber}
        </TableCell>
        <TableCell className={classes.bodyCell}>
          {!!stopId && !!routeId && (
            <ConditionalWrapper
              condition={!enableNavigation}
              wrapper={(children) => (
                <Tooltip
                  placement="top-start"
                  fontSize="small"
                  title={`You don't have access to ${depotCode} depot`}
                >
                  {children}
                </Tooltip>
              )}
            >
              <Link
                data-testid="view-button"
                onClick={() =>
                  enableNavigation && handleNavigate(routeId, stopId, depotCode)
                }
                component="button"
                variant="body2"
                className={classes.blueButton}
                disabled={!enableNavigation}
              >
                {VIEW}
              </Link>
            </ConditionalWrapper>
          )}
        </TableCell>
      </TableRow>
    );
  }
);

ParcelSearchResultRow.propTypes = {
  row: PropTypes.shape({
    depotCode: PropTypes.string.isRequired,
    routeCode: PropTypes.string.isRequired,
    displayNumber: PropTypes.string,
    sequenceNumber: PropTypes.string,
    routeId: PropTypes.string.isRequired,
    stopId: PropTypes.string,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  handleNavigate: PropTypes.func.isRequired,
  depots: PropTypes.arrayOf(
    PropTypes.shape({
      depotCode: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const ParcelSearchResult = ({ results, errors, onClose }) => {
  const classes = useStyles();
  const navigate = useAsyncNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const depots = useSelector(getUserDepots);

  const handleNavigate = useCallback(
    async (newRouteId, stopId, depotCode) => {
      const { date } = parseQuery(location.search);

      await navigate({
        pathname: `${ROUTES_PAGE}`,
        search: stringifyQuery({
          date,
          depot: depotCode,
          routeId: newRouteId,
          detailVisible: true,
        }),
      });

      if (onClose) {
        onClose();
      }
      dispatch(clearSelection());
      dispatch(updateSelectedStops([stopId]));
    },
    [dispatch, navigate, location.search, onClose]
  );

  const renderContent = useMemo(() => {
    if (errors?.parcelNumber?.message) {
      return (
        <Text className={classes.errorText}>{errors.parcelNumber.message}</Text>
      );
    }

    if (!results) {
      return null;
    }

    if (!results.length) {
      return <Text className={classes.errorText}>{NO_PARCELS_FOUND}</Text>;
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell className={classes.headerCell}>
                {DEPOT_NUMBER}
              </TableCell>
              <TableCell className={classes.headerCell}>{ROUTE}</TableCell>
              <TableCell className={classes.headerCell}>{STOP}</TableCell>
              <TableCell className={classes.actionHeaderCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((row, index) => (
              <ParcelSearchResultRow
                key={index}
                row={row}
                classes={classes}
                handleNavigate={handleNavigate}
                depots={depots}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [classes, results, errors, handleNavigate, depots]);

  return <View className={classes.container}>{renderContent}</View>;
};

ParcelSearchResult.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      depotCode: PropTypes.string.isRequired,
      routeCode: PropTypes.string.isRequired,
      displayNumber: PropTypes.string,
      sequenceNumber: PropTypes.string,
      routeId: PropTypes.string.isRequired,
      stopId: PropTypes.string,
    })
  ),
  errors: PropTypes.shape({
    parcelError: PropTypes.string,
  }),
  onClose: PropTypes.func,
};

export default memo(ParcelSearchResult);
